import { useEffect, useRef, useCallback } from 'react';
import { debugLogger } from '@/utils/debugLogger';

interface UseInfiniteScrollProps {
  onLoadMore: () => void;
  hasMore: boolean;
  isLoading?: boolean;
  threshold?: number; // Pixels antes do final para trigger
}

export const useInfiniteScroll = ({
  onLoadMore,
  hasMore,
  isLoading = false,
  threshold = 300
}: UseInfiniteScrollProps) => {
  const observerTarget = useRef<HTMLDivElement>(null);

  const handleObserver = useCallback((entries: IntersectionObserverEntry[]) => {
    const [target] = entries;
    
    if (target.isIntersecting && hasMore && !isLoading) {
      debugLogger.debug('InfiniteScroll', 'Loading more profiles (intersection detected)');
      onLoadMore();
    }
  }, [hasMore, isLoading, onLoadMore]);

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;

    const options = {
      root: null,
      rootMargin: `${threshold}px`,
      threshold: 0
    };

    const observer = new IntersectionObserver(handleObserver, options);
    observer.observe(element);

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [handleObserver, threshold]);

  return { observerTarget };
};
